import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Router } from '@angular/router';
import { ScreenSizeDetectorService, UiKitModule } from '@serviceplan/ui-kit';
import {
  anyAsset,
  AppSettings,
  AssetTagsService,
  AssetUIModel,
  CanPipe,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  FilterAllOptions,
  HasRoleDirective,
  ValueListsService,
} from '@thehubz/client/core';
import { Asset, AssetsApiService, NotifyOptions, Tags, UsersTaggingApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';
import { Subscription, tap } from 'rxjs';
import { AssetCommonService } from '../../../../services/asset-common.service';
import { AssetNotificationsComponent } from '../../asset-notifications/asset-notifications.component';
import { CustomAssetFieldsComponent } from '../../custom-asset-fields/custom-asset-fields.component';

@Component({
  selector: 'thehubz-asset-tagging-n-taxonomy-metadata',
  templateUrl: './asset-tagging-n-taxonomy-metadata.component.html',
  styleUrls: ['./asset-tagging-n-taxonomy-metadata.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    ClientCoreModule,
    UiKitModule,
    AssetNotificationsComponent,
    CustomAssetFieldsComponent,
    HasRoleDirective,
  ],
})
export class AssetTaggingNTaxonomyMetadataComponent implements OnChanges, OnDestroy {
  @Input() assetInfo!: AssetUIModel;
  @Input() sharingFlag?: boolean;
  @ViewChild('cusFieldsTest') cusFieldsTest!: CustomAssetFieldsComponent;

  AnyAsset = anyAsset;

  tagSub?: Subscription;
  editFlag = false;
  customFormInvalidCheck!: boolean;
  updateAssetForm: FormGroup;
  /** Keep the original state of the tags */
  currentTagsState: string[] = [];
  /** Tags for chip display */
  tags: string[] = [];
  /** Auto complete tag data */
  autoCompTags: Tags[] = [];
  tagPlaceHolderText = $localize`Search tags...`;
  chipInputTooltipText = $localize`Press \'Enter\' or \'Comma\' to add tags`;
  saveProcess = false;
  tagHttpCallInProgress = false;

  brandingKey!: CustomClients;
  CustomClients = CustomClients;

  /** Store all tags associated with asset */
  private tagsInfo: Tags[] = [];

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private router: Router,
    protected canPipe: CanPipe,
    private _formBuilder: FormBuilder,
    private valueListsService: ValueListsService,
    private assetsService: AssetsApiService,
    private assetTagsService: AssetTagsService,
    private commonUtilityLib: CommonUtilityLib,
    private assetCommonService: AssetCommonService,
    private usersTaggingApiService: UsersTaggingApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {
    this.brandingKey = this.env.brandingKey ?? CustomClients.Standard;
    this.updateAssetForm = this._formBuilder.group({
      tags: [],
    });
    if ([CustomClients.LufthansaMediacenter, CustomClients.LufthansaMedialounge].includes(this.brandingKey)) {
      this.chipInputTooltipText = '';
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    this.assetCommonService.metadataEditable.next(this.editFlag);
    this.assetInfo = changes['assetInfo'].currentValue;
    await this.addTags();
    this.patchFormValues();
    this.tagSub = this.assetTagsService
      .fetchTagsForAutoComp(this.updateAssetForm.get(AssetMetadata.General.TAGS))
      ?.pipe(tap((data) => (this.autoCompTags = data)))
      .subscribe();
  }

  patchFormValues() {
    this.updateAssetForm.patchValue({
      tags: this.tagsInfo,
    });
  }

  /**
   *  This function will update the asset usage right as well as custom fields
   *  elements which are coming from custom fields child components
   */
  async saveAssetInfo() {
    if (this.updateAssetForm.valid) {
      this.saveProcess = true;
      const formValues: AnyType = {};
      const customDataFields = this.cusFieldsTest.createCustomFieldForm.getRawValue();
      formValues['customFields'] = {};
      if (this.assetInfo?.customFields && !this.commonUtilityLib.isEmptyObject(this.assetInfo?.customFields)) {
        formValues['customFields'] = this.assetInfo.customFields;
      }
      Object.keys(customDataFields).forEach(function (key) {
        if (customDataFields[key]) formValues['customFields'][key] = customDataFields[key]._id ?? customDataFields[key];
      });
      this.currentTagsState = [...this.tags];
      formValues.tags = await this.assetCommonService.modifiedTags(this.tags, this.tagsInfo);
      this.updateAssetInfo(formValues);
    }
  }

  updateAssetInfo(formData: AnyType) {
    this.assetsService.assetsControllerUpdate(this.assetInfo?._id, formData).subscribe({
      next: (data: Asset) => {
        if (data) {
          // from thehubz-custom-asset-fields: notify users tagged in sp-editor component
          const editorTaggedUserList = this.cusFieldsTest.taggedUser;
          const editorDescriptionText = this.cusFieldsTest.descriptionText;
          if (editorTaggedUserList && editorTaggedUserList.length) {
            const notifyOptions: NotifyOptions = {
              userInfo: editorTaggedUserList,
              callFromContext: '',
              contextPath: 'asset',
              contextId: data._id,
              contextName: data.name,
              editorDescription: editorDescriptionText,
            };
            this.usersTaggingApiService.usersTaggingControllerNotifyTaggedUsers(notifyOptions).subscribe();
          }
          if (data.tags) {
            data.tags.sort();
          }
          // deleting currentVersion from data (object returned from update) as update doesn't populate currentVersion
          delete data.currentVersion;
          this.assetInfo = { ...this.assetInfo, ...data };
          this.editFlag = !this.editFlag;
          this.assetCommonService.metadataEditable.next(this.editFlag);
          this.assetCommonService.assetDataSubject$.next(this.assetInfo);
          this.patchFormValues();
          this.saveProcess = false;
        }
      },
      error: (err) => {
        this.clientNotificationHandlerService.errorNotification(err);
        this.saveProcess = false;
      },
    });
  }

  editInformation() {
    this.editFlag = !this.editFlag;
    this.assetCommonService.metadataEditable.next(this.editFlag);
    if (this.cusFieldsTest.customFieldsFormData.formFields) {
      this.customFormInvalidCheck = this.cusFieldsTest.createCustomFieldForm.invalid;
      // until required fields are not filled custom form will remain invalid
      this.cusFieldsTest.createCustomFieldForm.valueChanges.subscribe(() => {
        this.customFormInvalidCheck = this.cusFieldsTest.createCustomFieldForm.invalid;
      });
    }
    // restore the tags on cancel edit
    if (!this.editFlag) {
      this.tags = [...this.currentTagsState];
      this.updateAssetForm.controls[AssetMetadata.General.TAGS].patchValue(this.tags);
      this.assetTagsService.updateTagsArray(this.tags);
      this.autoCompTags = [];
    }
  }

  /* add tags with page loads */
  async addTags() {
    if (this.assetInfo?.tags?.length) {
      let tagValueFlag = false;
      const assetTags: string[] = [];
      if (this.assetInfo.tags) {
        this.assetInfo.tags.forEach((tag: AnyType) => {
          if (tag._id) {
            tagValueFlag = true;
          }
          assetTags.push(tag._id || tag);
        });
      }
      const tagIds: string[] = assetTags;

      if (tagValueFlag) {
        this.tagsInfo = this.assetInfo.tags as AnyType[];
      } else {
        this.tagHttpCallInProgress = true;
        try {
          // @TODO: tags caching fails on sharing-client. When done, the condition could be removed
          this.tagsInfo = await this.valueListsService.listTags(tagIds);
        } catch (error) {
          console.error('Error fetching tags:', error);
        } finally {
          this.tagHttpCallInProgress = false;
        }
      }

      // Map the sorted array to get tag names
      this.tagsInfo.forEach((tag) => {
        if (tag.name && !this.tags.includes(tag.name)) {
          this.tags.push(tag.name);
          this.currentTagsState.push(tag.name);
        }
      });
      this.assetTagsService.updateTagsArray(this.tags);
    }
  }

  add(event: string): void {
    if (event) {
      if (this.tags.includes(event?.toLowerCase())) {
        return;
      }
      this.tags.push(event);
      this.tags.sort((a, b) => a.localeCompare(b));
      this.updateAssetForm.controls[AssetMetadata.General.TAGS].patchValue(this.tags);
      this.assetTagsService.updateTagsArray(this.tags);
    }
  }

  removeTag(tag: string): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
      this.tagsInfo.splice(index, 1);
      this.tags.sort((a, b) => a.localeCompare(b));
      this.updateAssetForm.controls[AssetMetadata.General.TAGS].patchValue(this.tags);
    }
  }

  clickOnChip(chipValue: { _id: string; name: string }) {
    if (this.sharingFlag) return;
    const assetTagFilterObj: FilterAllOptions = {
      filters: {
        status: {},
        tags: {
          $all: [chipValue._id],
        },
      },
      sort: {
        createdAt: -1,
      },
      limit: 48,
      skip: 0,
      selectedFilterData: {
        tags: [chipValue],
      },
      searchedText: '',
    };
    const statusList = this.assetCommonService.getAssetStatusList(this.canPipe, this.AnyAsset);
    assetTagFilterObj['filters']['status'] = { $in: statusList };
    sessionStorage.setItem(AppSettings.ASSET_FILTER_STRING, JSON.stringify(assetTagFilterObj));
    this.router.navigate(['assets']);
  }

  ngOnDestroy(): void {
    this.tagSub?.unsubscribe();
  }
}
